import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { stdObject } from "../../../data/types/stdObject";
import { objectToArray } from "../../../utils/objectToArray";
import { useMainContext } from "../../../context/MainContext";
import { colors } from "../../../data/colors";
import { purchasesCodes, salesCodes } from "../../../data/main";

const score = ({ CODART, NOMART }: stdObject, lowValue: string) => {
  CODART = CODART.toLowerCase();
  NOMART = NOMART.toLowerCase();
  if (CODART === lowValue) return 0;
  if (NOMART === lowValue) return 1;
  if (CODART.startsWith(lowValue)) return 2;
  if (NOMART.startsWith(lowValue)) return 3;
  if (CODART.includes(lowValue)) return 4;
  if (NOMART.includes(lowValue)) return 5;
  return 6;
};

export default function QuickSearch({
  type,
  value = "",
  resList,
  setResList,
  isDown = true,
  selectPos = 0,
  setSelectPos = 0,
  cleanInput,
}: {
  type: string;
  value: string;
  resList: stdObject[];
  setResList: any;
  isDown: boolean;
  selectPos: number;
  setSelectPos: any;
  cleanInput: () => void;
}) {
  const { t } = useTranslation();
  const { updateDocData } = useMainContext();
  const art: stdObject = useSelector((state: RootState) => state.data.art);
  const itemsList: Array<stdObject> = useMemo(
    () => objectToArray("CODART", art),
    [art],
  );
  const [loading, setLoading] = useState(false);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);
  const quickCounter = useRef<ReturnType<typeof setTimeout> | null>(null);

  const findItems = async () => {
    const eanItem = itemsList.find((e: stdObject) => e.EANART === value);
    if (
      eanItem &&
      (purchasesCodes.includes(type) ||
        (salesCodes.includes(type) && eanItem.TIPART !== 6))
    ) {
      cleanInput();
      updateDocData(eanItem, "ART");
      setResList([]);
    } else {
      const lowValue = value.toLowerCase();
      let newList: Array<stdObject> = itemsList
        .filter(
          (e: stdObject) =>
            (e.CODART.toLowerCase().includes(lowValue) ||
              e.NOMART.toLowerCase().includes(lowValue)) &&
            (purchasesCodes.includes(type) ||
              (salesCodes.includes(type) && e.TIPART !== 6)),
        )
        .sort((a: stdObject, b: stdObject) => {
          const diff = score(a, lowValue) - score(b, lowValue);
          if (diff !== 0) return diff;
          const keyDiff = a.CODART.localeCompare(b.CODART);
          if (keyDiff !== 0) return keyDiff;
          return a.NOMART.localeCompare(b.NOMART);
        });
      if (newList.length > 50) newList.length = 50;
      setSelectPos(0);
      setResList(newList);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (quickCounter.current) clearTimeout(quickCounter.current);
    if (value !== "") {
      if (!loading) setLoading(true);
      quickCounter.current = setTimeout(findItems, 500);
    }
    return () => {
      if (quickCounter.current) clearTimeout(quickCounter.current);
    };
  }, [value]);

  useEffect(() => {
    const currentRow = rowRefs.current[selectPos];

    if (currentRow) {
      currentRow.scrollIntoView({
        behavior: "smooth",
        block: "center", // "start" | "center" | "end" | "nearest"
      });
      currentRow.focus();
    }
  }, [selectPos]);

  return value !== "" ? (
    <div
      className="quickSearch"
      style={{
        [isDown ? "top" : "bottom"]: 30,
        overflow: loading ? "hidden" : "auto",
      }}
    >
      {loading ? (
        <div className="spinner" style={{ width: 20, height: 20 }} />
      ) : resList.length ? (
        <table cellPadding={10} cellSpacing={0} style={{ cursor: "pointer" }}>
          <tbody>
            {resList.map((e: stdObject, index: number) => (
              <tr
                key={index}
                ref={(el) => {
                  rowRefs.current[index] = el;
                }}
                style={{
                  backgroundColor:
                    index === selectPos
                      ? colors.lightBlue
                      : index % 2 === 0
                        ? colors.white
                        : colors.lightGrey,
                }}
                onClick={() => {
                  setResList([]);
                  updateDocData(e, "ART");
                  cleanInput();
                }}
              >
                <td>{e.CODART}</td>
                <td>{e.NOMART}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div>{t("p.noResults")}</div>
      )}
    </div>
  ) : (
    <></>
  );
}
